import React from 'react'
import { Config, Data, DefaultRootFieldProps } from '@puckeditor/core'
import {
  POLL_FIELDS,
  POLL_INITIAL_DATA,
  PollContainerProps,
  PuckPollContainer,
} from './components/PollContainer/PollContainer'
import {
  PuckPollQuestion,
  PollQuestionProps,
} from './components/PollQuestion/PollQuestion'
import {
  PuckPollAnswer,
  PollAnswerProps,
} from './components/PollAnswer/PollAnswer'
import {
  PuckPollProductPanel,
  PollProductPanelProps,
} from './components/PollProductPanel/PollProductPanel'
import {
  PuckPollProduct,
  PollProductProps,
} from './components/PollProduct/PollProduct'
import {
  PuckPollNextQuestionButton,
  PollNextQuestionButtonProps,
} from './components/PollNextQuestionButton/PollNextQuestionButton'
import {
  PuckPollDropdownButton,
  PollDropdownButtonProps,
} from './components/PollDropdownButton/PollDropdownButton'
import {
  PuckPollSkipButton,
  PollSkipButtonProps,
} from './components/PollSkipButton/PollSkipButton'
import {
  PuckPollProgressBar,
  PollProgressBarProps,
} from './components/PollProgressBar/PollProgressBar'
import {
  PuckPollSocialShare,
  PollSocialShareProps,
} from './components/PollSocialShare/PollSocialShare'
import {
  PuckPollStartScreen,
  PollStartScreenProps,
  PuckPollStartScreenCard,
  PollStartScreenCardProps,
  PuckPollStartScreenButton,
  PollStartScreenButtonProps,
} from './components/PollStartScreen/PollStartScreen'
import {
  LISTICLE_FIELDS,
  LISTICLE_INITIAL_DATA,
  ListicleContainerProps,
  PuckListicleContainer,
} from './components/ListicleContainer/ListicleContainer'
import {
  PuckListicleItem,
  ListicleItemProps,
} from './components/ListicleItem/ListicleItem'
import {
  PuckListicleImageComparison,
  ListicleImageComparisonProps,
} from './components/ListicleImageComparison/ListicleImageComparison'
import {
  PuckListicleMenu,
  ListicleMenuProps,
} from './components/ListicleMenu/ListicleMenu'
import {
  PuckListicleVoting,
  ListicleVotingProps,
} from './components/ListicleVoting/ListicleVoting'
import {
  REFERRAL_FIELDS,
  REFERRAL_INITIAL_DATA,
  ReferralContainerProps,
  PuckReferralContainer,
} from './components/ReferralContainer/ReferralContainer'
import {
  PuckReferralCard,
  ReferralCardProps,
} from './components/ReferralCard/ReferralCard'
import {
  PuckReferralButton,
  ReferralButtonProps,
} from './components/ReferralButton/ReferralButton'
import {
  PuckReferralDropdown,
  ReferralDropdownProps,
} from './components/ReferralDropdown/ReferralDropdown'
import {
  PQ_FIELDS,
  PQ_INITIAL_DATA,
  PQContainerProps,
  PuckPQContainer,
} from './components/PQContainer/PQContainer'
import {
  PuckPQQuestion,
  PQQuestionProps,
} from './components/PQQuestion/PQQuestion'
import { PuckPQAnswer, PQAnswerProps } from './components/PQAnswer/PQAnswer'
import { PuckPQResult, PQResultProps } from './components/PQResult/PQResult'
import {
  THEME_CATEGORY_NAMES,
  ThemeComponents,
  ThemeCustomFields,
  createPuckConfig,
  defineComponent,
} from 'breaktime-theme/src/puck/theme-config'
import {
  offersSectionConfig,
  OffersSectionProps,
} from 'breaktime-theme/src/steps/promo/OffersSectionBlock'

type Components = ThemeComponents & {
  PollContainer: PollContainerProps
  PollQuestion: PollQuestionProps
  PollAnswer: PollAnswerProps
  PollProductPanel: PollProductPanelProps
  PollProduct: PollProductProps
  PollNextQuestionButton: PollNextQuestionButtonProps
  PollDropdownButton: PollDropdownButtonProps
  PollSkipButton: PollSkipButtonProps
  PollProgressBar: PollProgressBarProps
  PollSocialShare: PollSocialShareProps
  /** Canonical shared share block; PollSocialShare is the hidden legacy
   * alias kept so existing saved steps keep rendering. */
  SocialShare: PollSocialShareProps
  PollStartScreen: PollStartScreenProps
  PollStartScreenCard: PollStartScreenCardProps
  PollStartScreenButton: PollStartScreenButtonProps
  ListicleContainer: ListicleContainerProps
  ListicleItem: ListicleItemProps
  ListicleImageComparison: ListicleImageComparisonProps
  ListicleMenu: ListicleMenuProps
  ListicleVoting: ListicleVotingProps
  OffersSection: OffersSectionProps
  ReferralContainer: ReferralContainerProps
  ReferralCard: ReferralCardProps
  /** Canonical shop/CTA button; ReferralButton is the hidden legacy alias. */
  ShopButton: ReferralButtonProps
  ReferralButton: ReferralButtonProps
  ReferralDropdown: ReferralDropdownProps
  PQContainer: PQContainerProps
  PQQuestion: PQQuestionProps
  PQAnswer: PQAnswerProps
  PQResult: PQResultProps
}

type RootProps = {
  breakType: 'editorial' | 'sponsored'
  stepLabel: string
}

export type CustomFields = ThemeCustomFields

export type PuckData = Data<Components, RootProps & DefaultRootFieldProps>
export type PuckConfig = Config<{
  components: Components
  root: RootProps
  categories: [...typeof THEME_CATEGORY_NAMES, 'poll', 'listicle', 'referral', 'personalityQuiz', 'steps', 'hidden']
  fields: CustomFields
}>

export const config = createPuckConfig<PuckConfig>({
  components: {
    PollContainer: defineComponent<PollContainerProps>({
      label: 'Poll',
      // Authoring notes for the editor's AI agent (BR-5693): what it renders and how to compose it.
      ai: {
        description: 'A poll step: the reader votes, then sees result bars.',
        renders:
          'One card holding a progress bar, the questions one at a time, a Next Question button and an optional skip link. Editorial: light card on the page. Sponsored: dark translucent card over the background field.',
        rules: [
          'Put PollQuestion components in questions; each question holds its PollAnswer components in answers.',
          'Leave the other slots (beforeCard, afterVote, productPanel...) empty unless asked: they are optional extras.',
          '2 to 5 answers per question read best; keep answer text under about 30 characters.',
        ],
      },
      defaultProps: POLL_INITIAL_DATA,
      fields: POLL_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial = (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = POLL_FIELDS
          return rest
        }
        return POLL_FIELDS
      },
      render: props => <PuckPollContainer {...props} />,
    }),
    PollQuestion: {
      ...PuckPollQuestion,
      ai: {
        description: 'One poll question.',
        fields: { textAnswered: 'Shown after voting, sponsored steps only.' },
        rules: ['headerContent is an optional image/video above the question.'],
      },
    },
    PollAnswer: {
      ...PuckPollAnswer,
      ai: {
        description:
          'One answer option; after a vote it shows its result bar and percentage.',
        rules: [
          'beforePercentage / afterPercentage are optional slots around the percentage; leave them empty unless asked.',
        ],
      },
    },
    PollProductPanel: {
      ...PuckPollProductPanel,
      ai: {
        description:
          'Product row (a headline and PollProduct items) for the poll footer slot, PollContainer.productPanel.',
      },
    },
    PollProduct: {
      ...PuckPollProduct,
      ai: {
        description:
          'One product in a PollProductPanel: image, label and an optional PollDropdownButton of buy links.',
      },
    },
    PollNextQuestionButton: PuckPollNextQuestionButton,
    PollDropdownButton: {
      ...PuckPollDropdownButton,
      ai: {
        description:
          'Button that opens a menu of links, e.g. one per retailer.',
      },
    },
    PollSkipButton: {
      ...PuckPollSkipButton,
      ai: {
        description:
          'Small link that lets the reader skip the poll; goes in PollContainer.skipButton.',
      },
    },
    PollProgressBar: PuckPollProgressBar,
    PollSocialShare: PuckPollSocialShare,
    SocialShare: {
      ...PuckPollSocialShare,
      ai: {
        ...PuckPollSocialShare.ai,
        description:
          'Share buttons (Facebook, X, Pinterest, email, WhatsApp, copy link); each is turned on by its bool field.',
      },
    },
    PollStartScreen: defineComponent<PollStartScreenProps>({
      ...PuckPollStartScreen,
      ai: {
        description:
          'Intro screen shown before a step starts, holding a PollStartScreenCard (headline, body, start button).',
        rules: [
          "Goes in a container's startScreen slot. It comes with its card: edit that card with update_component, do not add another.",
        ],
      },
      resolveFields: (_data, { appState }) => {
        const isEditorial = (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = PuckPollStartScreen.fields!
          return rest
        }
        return PuckPollStartScreen.fields!
      },
    }),
    PollStartScreenCard: PuckPollStartScreenCard,
    PollStartScreenButton: PuckPollStartScreenButton,
    ListicleContainer: defineComponent<ListicleContainerProps>({
      label: 'Listicle',
      ai: {
        description:
          'Listicle step: numbered items shown as a list or a slideshow (displayFormat).',
        rules: [
          "Put ListicleItem components in items, and fill each item's rows with blocks (Image, Richtext...).",
        ],
      },
      defaultProps: LISTICLE_INITIAL_DATA,
      fields: LISTICLE_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial =
          (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = LISTICLE_FIELDS
          return rest
        }
        return LISTICLE_FIELDS
      },
      render: props => <PuckListicleContainer {...props} />,
    }),
    ListicleItem: {
      ...PuckListicleItem,
      ai: {
        description:
          'One listicle entry: subhead, content rows (topRow, middleLeft and middleRight, bottomRow) and a voting slot.',
        fields: {
          voting: 'Put a ListicleVoting here to let readers vote on this item.',
        },
      },
    },
    ListicleImageComparison: {
      ...PuckListicleImageComparison,
      ai: {
        description:
          'Before and after images the reader compares with a slider; for a ListicleItem row.',
      },
    },
    ListicleMenu: {
      ...PuckListicleMenu,
      ai: {
        description:
          'Dropdown button of links (e.g. where to buy) for a ListicleItem row.',
      },
    },
    ListicleVoting: {
      ...PuckListicleVoting,
      ai: {
        description:
          'Up and down vote buttons with result bars; goes in ListicleItem.voting.',
      },
    },
    OffersSection: {
      ...offersSectionConfig,
      ai: {
        description:
          'Grid of the offers of a PROMO step, with optional filter and sort.',
        fields: {
          stepId: 'Empty uses the step this block is in.',
        },
      },
    },
    ReferralContainer: defineComponent<ReferralContainerProps>({
      label: 'Referral',
      ai: {
        description:
          'Referral step: grid of offer or product cards (1 to 3 columns) with a headline, intro copy and optional voting.',
        rules: [
          'Put ReferralCard components in items; header and footer take any other content.',
        ],
        fields: {
          introCopy: 'HTML string.',
          bottomText: 'HTML string.',
        },
      },
      defaultProps: REFERRAL_INITIAL_DATA,
      fields: REFERRAL_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial =
          (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = REFERRAL_FIELDS
          return rest
        }
        return REFERRAL_FIELDS
      },
      render: props => <PuckReferralContainer {...props} />,
    }),
    ReferralCard: {
      ...PuckReferralCard,
      ai: {
        description:
          'One referral card: media, top and bottom text, and an action (ShopButton or ReferralDropdown).',
        fields: {
          topText: 'HTML string.',
          bottomText: 'HTML string.',
          mediaHover: 'Optional media shown instead of media while hovered.',
          pqHideResults:
            'Hides the card for chosen quiz results; leave it to the person.',
        },
      },
    },
    ShopButton: {
      ...PuckReferralButton,
      ai: {
        description:
          'Shop or call-to-action link button for referral cards and quiz results.',
        fields: {
          buttonText:
            'Empty hides the button; in a referral card the url still links the card image.',
          padding: 'CSS shorthand, e.g. 12px 24px.',
        },
      },
    },
    ReferralButton: PuckReferralButton,
    ReferralDropdown: {
      ...PuckReferralDropdown,
      ai: {
        description:
          'Dropdown menu of product links, for a ReferralCard action or PQResult buttons.',
      },
    },
    PQContainer: defineComponent<PQContainerProps>({
      label: 'Personality Quiz',
      ai: {
        description:
          'Personality quiz step: questions, then the result that matches the answers.',
        rules: [
          'Put PQQuestion components in questions (each with PQAnswer components in answers) and PQResult components in results.',
          'You cannot link answers to results: after building, ask the person to choose the result on each PQAnswer.',
        ],
      },
      defaultProps: PQ_INITIAL_DATA,
      fields: PQ_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial =
          (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = PQ_FIELDS
          return rest
        }
        return PQ_FIELDS
      },
      render: props => <PuckPQContainer {...props} />,
    }),
    PQQuestion: {
      ...PuckPQQuestion,
      ai: {
        description:
          'One quiz question card: optional header media, the question and its PQAnswer components.',
        fields: {
          text: 'HTML string.',
          answerWidth:
            'Width of every answer, e.g. 32% for three image answers per row.',
        },
      },
    },
    PQAnswer: {
      ...PuckPQAnswer,
      ai: {
        description:
          'One quiz answer; empty text with an image in media makes an image-only answer.',
        fields: {
          text: 'HTML string.',
          resultId:
            'Internal id of the PQResult this answer counts toward. You cannot see those ids: leave it for the person to pick.',
          resultIds:
            'Decision Tree mode, same as resultId: leave it for the person to pick.',
        },
      },
    },
    PQResult: {
      ...PuckPQResult,
      ai: {
        description:
          'One quiz outcome card: title, description, media and buttons (ShopButton, ReferralDropdown, Button).',
        fields: {
          title: 'HTML string.',
          desc: 'HTML string.',
        },
      },
    },
  },
  root: {
    fields: {
      breakType: {
        type: 'radio',
        label: 'Break Type',
        options: [
          { label: 'Editorial', value: 'editorial' },
          { label: 'Sponsored', value: 'sponsored' },
        ],
      },
      stepLabel: {
        type: 'text',
        label: 'Step Label',
        visible: false,
      },
    },
    defaultProps: {
      stepLabel: 'Poll',
      breakType: 'editorial',
    },
    render: ({ children }) => <>{children}</>,
  },
  categories: {
    hidden: {
      visible: false,
      components: [
        'PollNextQuestionButton',
        'PollStartScreenCard',
        'PollStartScreenButton',
        'PollProgressBar',
        // legacy aliases kept registered for old saved steps
        'PollSocialShare',
        'ReferralButton',
      ],
    },
    poll: {
      title: 'Poll',
      components: ['PollContainer'],
      subcategories: {
        questions: {
          title: 'Questions & Answers',
          components: ['PollQuestion', 'PollAnswer'],
        },
        products: {
          title: 'Products',
          components: ['PollProductPanel', 'PollProduct'],
        },
        actions: {
          title: 'Actions',
          components: ['PollDropdownButton', 'PollSkipButton'],
        },
      },
    },
    listicle: {
      title: 'Listicle',
      components: [
        'ListicleContainer',
        'ListicleItem',
        'ListicleImageComparison',
        'ListicleMenu',
        'ListicleVoting',
      ],
    },
    referral: {
      title: 'Referral',
      components: [
        'ReferralContainer',
        'ReferralCard',
        'ReferralDropdown',
      ],
    },
    personalityQuiz: {
      title: 'Personality Quiz',
      components: ['PQContainer', 'PQQuestion', 'PQAnswer', 'PQResult'],
    },
    // Shared cross-step blocks (start screen, social share, …)
    steps: {
      title: 'Steps',
      components: [
        'PollStartScreen',
        'SocialShare',
        'ShopButton',
        'OffersSection',
      ],
    },
  },
})
